import {
  ArrowDown,
  ArrowUpRight,
  BookOpen,
  GraduationCap,
  HeartHandshake,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

type Ministry = {
  title: string;
  description: string;
  image: string;
  alt: string;
  href?: string;
  action?: string;
};

const redPastoralYouthPath = "red-pastoral-juvenil/";
const nextGenPath = "nuevas-generaciones/";

type MinistryGroup = {
  id: string;
  number: string;
  title: string;
  kicker: string;
  description: string;
  icon: typeof UsersRound;
  ministries: Ministry[];
};

const featuredMinistries: Ministry[] = [
  {
    title: "Red Pastoral Juvenil",
    description:
      "Un espacio para conectar, formar y movilizar a la nueva generación en todo Costa Rica.",
    image:
      assetUrl("youth_ministry_worship.png"),
    alt: "Jóvenes reunidos en una celebración de adoración",
    href: redPastoralYouthPath,
    action: "Conocer la red",
  },
  {
    title: "Exploradores del Rey",
    description:
      "Formación cristiana, carácter, servicio y aventura para niños y adolescentes.",
    image:
      assetUrl("children_ministry_group.png"),
    alt: "Grupo de niños compartiendo en comunidad",
    href: "exploradores-del-rey/",
    action: "Explorar el ministerio",
  },
  {
    title: "Nuevas Generaciones",
    description:
      "Recursos y acompañamiento para una fe que crece desde la niñez hasta la juventud.",
    image:
      assetUrl("children_power_bible_reading.png"),
    alt: "Niña aprendiendo con una Biblia infantil",
    href: nextGenPath,
    action: "Descubrir la iniciativa",
  },
];

const ministryGroups: MinistryGroup[] = [
  {
    id: "adultos-familias",
    number: "01",
    title: "Adultos y familias",
    kicker: "Fe para cada etapa",
    description:
      "Comunidades que fortalecen la vida espiritual, los vínculos familiares y una vocación de servicio que permanece.",
    icon: HeartHandshake,
    ministries: [
      {
        title: "Ministerio a los hombres",
        description:
          "Capacitamos a los hombres para servir con madurez en sus familias, comunidades e iglesias.",
        image:
          assetUrl("men_ministry_group.webp"),
        alt: "Grupo de hombres conversando y creciendo en comunidad",
      },
      {
        title: "Ministerio a las mujeres",
        description:
          "Animamos a las mujeres a cumplir su llamado e impactar el mundo mediante la fe, el servicio y el liderazgo.",
        image:
          assetUrl("women_ministry_fellowship.png"),
        alt: "Mujeres compartiendo y sonriendo en comunidad",
      },
      {
        title: "Jóvenes adultos",
        description:
          "Acompañamos a los jóvenes adultos a escuchar la voz de Dios, descubrir su propósito y vivir su vocación.",
        image:
          assetUrl("young_adults_ministry.png"),
        alt: "Grupo de jóvenes adultos reunidos",
      },
      {
        title: "Adultos mayores",
        description:
          "Creamos oportunidades de enriquecimiento espiritual, conexión y servicio para personas de 55 años o más.",
        image:
          assetUrl("senior_adults_ministry.webp"),
        alt: "Adultos mayores compartiendo con alegría",
      },
    ],
  },
  {
    id: "jovenes",
    number: "02",
    title: "Jóvenes",
    kicker: "Una generación con propósito",
    description:
      "Experiencias que ayudan a los jóvenes a profundizar su fe, desarrollar sus dones y participar activamente en la misión de Dios.",
    icon: Sparkles,
    ministries: [
      {
        title: "Red Pastoral Juvenil",
        description:
          "Conectamos e inspiramos a los jóvenes para crecer en su fe y descubrir el propósito que tienen en Dios.",
        image:
          assetUrl("youth_ministry_worship.png"),
        alt: "Jóvenes en una celebración de adoración",
        href: redPastoralYouthPath,
        action: "Conocer la red",
      },
      {
        title: "Bellas Artes",
        description:
          "Cultivamos la creatividad y la expresión artística como parte del crecimiento espiritual de los jóvenes.",
        image:
          assetUrl("youth_fine_arts.webp"),
        alt: "Joven desarrollando una expresión artística",
      },
      {
        title: "Speed the Light",
        description:
          "Movilizamos a los jóvenes para apoyar las misiones y responder a necesidades ministeriales vitales.",
        image:
          assetUrl("youth_speed_the_light.png"),
        alt: "Jóvenes participando en una iniciativa misionera",
      },
      {
        title: "Bible Quiz",
        description:
          "Desafiamos a los adolescentes a profundizar en la Biblia mediante aprendizaje y competencias amistosas.",
        image:
          assetUrl("youth_bible_quiz.png"),
        alt: "Joven leyendo la Biblia al aire libre",
      },
    ],
  },
  {
    id: "ninos",
    number: "03",
    title: "Niños",
    kicker: "Raíces firmes desde el inicio",
    description:
      "Ministerios seguros, alegres y bíblicos que ayudan a cada niño a conocer a Jesús, servir a otros y crecer con propósito.",
    icon: UsersRound,
    ministries: [
      {
        title: "Exploradores del Rey",
        description:
          "Formamos carácter, habilidades y una vida de servicio mediante experiencias de aprendizaje y aventura.",
        image:
          assetUrl("children_ministry_group.png"),
        alt: "Niños sonriendo durante una actividad de ministerio",
        href: "exploradores-del-rey/",
        action: "Explorar el ministerio",
      },
      {
        title: "Nuevas Generaciones",
        description:
          "Equipamos a iglesias y familias para acompañar la fe de la próxima generación con intención y cercanía.",
        image:
          assetUrl("children_faith_case_parent.png"),
        alt: "Familia compartiendo con una niña al aire libre",
        href: nextGenPath,
        action: "Descubrir la iniciativa",
      },
      {
        title: "BGMC",
        description:
          "Inspiramos a los niños a amar a Jesús, amar a las naciones y vivir una generosidad con propósito.",
        image:
          assetUrl("children_bgmc_circle.png"),
        alt: "Niños reunidos en círculo mirando hacia la cámara",
      },
      {
        title: "Biblia de Poder para Niños",
        description:
          "Despertamos la pasión por la Palabra de Dios con historias, actividades y lecciones memorables.",
        image:
          assetUrl("children_power_bible_reading.png"),
        alt: "Niña leyendo una Biblia infantil con atención",
      },
    ],
  },
  {
    id: "lideres-ministros",
    number: "04",
    title: "Líderes y ministros",
    kicker: "Equipados para servir",
    description:
      "Procesos, recursos y relaciones que fortalecen a quienes lideran iglesias saludables y acompañan a sus comunidades.",
    icon: BookOpen,
    ministries: [
      {
        title: "Red de Multiplicación de Iglesias",
        description:
          "Empoderamos a líderes para establecer y fortalecer iglesias saludables en cada comunidad.",
        image:
          assetUrl("church_multiplication_network.png"),
        alt: "Líderes reunidos en capacitación y oración",
      },
      {
        title: "Recursos de liderazgo",
        description:
          "Compartimos herramientas prácticas, perspectivas bíblicas e historias que ayudan a liderar con claridad.",
        image:
          assetUrl("influence_magazine_resource.webp"),
        alt: "Publicación con recursos para el liderazgo",
      },
      {
        title: "Proceso Hechos 2",
        description:
          "Acompañamos un proceso transformador para fortalecer iglesias de manera espiritual, numérica y relacional.",
        image:
          assetUrl("acts_2_church_process.png"),
        alt: "Grupo de líderes participando en un taller",
      },
      {
        title: "Llamado",
        description:
          "Acompañamos a quienes han sido llamados al ministerio mediante formación, conexión y aliento espiritual.",
        image:
          assetUrl("calling_vocational_ministry.png"),
        alt: "Líder compartiendo durante una reunión ministerial",
      },
    ],
  },
  {
    id: "educacion",
    number: "05",
    title: "Educación",
    kicker: "Aprender para transformar",
    description:
      "Oportunidades de formación y experiencias que conectan el conocimiento, la vocación y una fe profundamente bíblica.",
    icon: GraduationCap,
    ministries: [
      {
        title: "Educación superior",
        description:
          "Orientación y recursos para quienes buscan continuar su formación en instituciones de las Asambleas de Dios.",
        image:
          assetUrl("education_ministry_students.png"),
        alt: "Estudiantes universitarios reunidos en un campus",
      },
      {
        title: "Estudio de la Tierra Santa",
        description:
          "Una invitación a comprender mejor la Biblia mediante el contexto, la historia y las tierras donde nació.",
        image:
          assetUrl("education_ministry_holy_land.png"),
        alt: "Vista de Jerusalén y los lugares de la Tierra Santa",
      },
    ],
  },
];

function MinistryLink({ ministry }: { ministry: Ministry }) {
  const href = homeUrl(ministry.href || "contacto/?tema=ministerios#contacto-formulario");
  const label = ministry.action || "Solicitar información";

  return (
    <a className="acad-min-card-link" href={href}>
      <span>{label}</span>
      <ArrowUpRight aria-hidden="true" />
    </a>
  );
}

function MinistryCard({
  ministry,
  lead,
  index,
}: {
  ministry: Ministry;
  lead: boolean;
  index: number;
}) {
  return (
    <article
      className={`acad-min-card ${lead ? "acad-min-card--lead" : ""}`}
      style={{ "--acad-card-delay": `${index * 70}ms` } as React.CSSProperties}
    >
      <img src={ministry.image} alt={ministry.alt} loading="lazy" decoding="async" />
      <div className="acad-min-card-shade" />
      <div className="acad-min-card-copy">
        <span className="acad-min-card-index">{String(index + 1).padStart(2, "0")}</span>
        <div>
          <h3>{ministry.title}</h3>
          <p>{ministry.description}</p>
          <MinistryLink ministry={ministry} />
        </div>
      </div>
    </article>
  );
}

export default function MinistriesPage() {
  return (
    <div className="acad-react-page acad-ministries-page">
      <Header />

      <main>
        <section className="acad-min-hero" data-nav="dark">
          <img
            className="acad-min-hero-image"
            src={assetUrl("Header_Ministries.jpg")}
            alt="Celebración de adoración y alabanza"
            loading="eager"
            decoding="async"
          />
          <div className="acad-min-hero-overlay" />

          <div className="acad-min-shell acad-min-hero-content">
            <div className="acad-min-hero-copy">
              <span className="acad-min-eyebrow acad-min-eyebrow--light">ACAD Costa Rica</span>
              <h1>
                <span>Ministerios</span>
                <small>de las Asambleas de Dios</small>
              </h1>
              <p>
                Personas de todas las edades creciendo en Jesús, descubriendo su llamado y
                sirviendo a Costa Rica.
              </p>
            </div>

            <a className="acad-min-scroll-cue" href="#ministerios-destacados" aria-label="Explorar ministerios">
              <span>Explorar</span>
              <i><ArrowDown aria-hidden="true" /></i>
            </a>
          </div>

          <div className="acad-min-shell acad-min-hero-facts" aria-label="Resumen de ministerios">
            <span><strong>5</strong> áreas de servicio</span>
            <span><strong>18</strong> iniciativas</span>
            <span><strong>1</strong> misión compartida</span>
          </div>
        </section>

        <section id="ministerios-destacados" className="acad-min-intro">
          <div className="acad-min-shell">
            <div className="acad-min-intro-copy">
              <span className="acad-min-eyebrow">Ministerios destacados</span>
              <h2>Una iglesia para cada generación.</h2>
              <p>
                Servimos a las personas en cada etapa de la vida y equipamos a quienes han sido
                llamados a liderar. Encuentra el espacio donde puedes crecer, servir y compartir
                la esperanza de Jesús.
              </p>
            </div>

            <div className="acad-min-feature-grid">
              {featuredMinistries.map((ministry, index) => (
                <a
                  className={`acad-min-feature ${index === 0 ? "acad-min-feature--primary" : ""}`}
                  href={homeUrl(ministry.href)}
                  key={ministry.title}
                >
                  <img src={ministry.image} alt={ministry.alt} loading="lazy" decoding="async" />
                  <span className="acad-min-feature-shade" />
                  <span className="acad-min-feature-arrow"><ArrowUpRight aria-hidden="true" /></span>
                  <span className="acad-min-feature-copy">
                    <small>Ministerio nacional</small>
                    <strong>{ministry.title}</strong>
                    <em>{ministry.description}</em>
                    <b>{ministry.action}</b>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <nav className="acad-min-category-nav" aria-label="Áreas de ministerio">
          <div className="acad-min-shell">
            {ministryGroups.map((group) => (
              <a href={`#${group.id}`} key={group.id}>{group.title}</a>
            ))}
          </div>
        </nav>

        <section className="acad-min-directory" aria-labelledby="acad-min-directory-title">
          <div className="acad-min-shell acad-min-directory-heading">
            <span className="acad-min-eyebrow">Directorio de ministerios</span>
            <h2 id="acad-min-directory-title">Encuentra dónde crecer y servir.</h2>
          </div>

          {ministryGroups.map((group) => {
            const Icon = group.icon;

            return (
              <section className="acad-min-group" id={group.id} key={group.id}>
                <div className="acad-min-shell">
                  <header className="acad-min-group-header">
                    <span className="acad-min-group-number">{group.number}</span>
                    <div className="acad-min-group-title">
                      <span><Icon aria-hidden="true" /> {group.kicker}</span>
                      <h2>{group.title}</h2>
                    </div>
                    <p>{group.description}</p>
                  </header>

                  <div className={`acad-min-card-grid ${group.ministries.length === 2 ? "acad-min-card-grid--two" : ""}`}>
                    {group.ministries.map((ministry, index) => (
                      <MinistryCard
                        ministry={ministry}
                        lead={index === 0 && group.ministries.length > 2}
                        index={index}
                        key={ministry.title}
                      />
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
        </section>

        <section className="acad-min-cta">
          <div className="acad-min-shell acad-min-cta-inner">
            <div>
              <span className="acad-min-eyebrow acad-min-eyebrow--light">Estamos para ayudarte</span>
              <h2>¿Quieres conectar con un ministerio?</h2>
            </div>
            <a href={homeUrl("contacto/?tema=ministerios#contacto-formulario")}>
              Hablar con ACAD
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
